import {useEffect, useState} from 'react';
import {CombinedGraphQLErrors} from '@apollo/client/errors';
import type {Call} from '../../api/calls';

export function callAccessDenied(error: unknown): boolean {
	if (!CombinedGraphQLErrors.is(error)) return false;
	return error.errors.some((item) => {
		const original = item.extensions?.originalError;
		const status =
			original && typeof original === 'object' && 'statusCode' in original
				? original.statusCode
				: null;
		return (
			['UNAUTHORIZED', 'UNAUTHENTICATED', 'FORBIDDEN'].includes(String(item.extensions?.code)) ||
			status === 401 ||
			status === 403
		);
	});
}

export function callAvailabilityReason(reason?: string | null): string {
	return (
		(
			{
				DISABLED: 'Звонки временно недоступны',
				ROLE_DENIED: 'Начать звонок может владелец или менеджер селлера',
				CONNECTION_INACTIVE: 'Звонки доступны по действующему договору',
				COMPANY_BLOCKED: 'Для этой компании звонки недоступны',
				// BUSY — не общий лимит (его нет с SKS-750): занят этот чат или все, кто может ответить.
				BUSY: 'В этом чате уже идёт звонок или все, кто может ответить, заняты другими звонками. Попробуйте позже',
				OUTSIDE_WORKING_HOURS: 'Можно позвонить в рабочее время склада',
			} as Record<string, string>
		)[reason ?? ''] ?? 'Звонок сейчас недоступен'
	);
}

const fallbackDevice = crypto.randomUUID();
export function callDeviceId(): string {
	try {
		const value = sessionStorage.getItem('wn.call-device') ?? fallbackDevice;
		sessionStorage.setItem('wn.call-device', value);
		return value;
	} catch {
		return fallbackDevice;
	}
}
export function formatCallDuration(seconds: number): string {
	return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}
export function useCallClock(startedAt?: string | null, countdown = false): number {
	const [now, setNow] = useState(Date.now);
	useEffect(() => {
		const timer = window.setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(timer);
	}, []);
	return startedAt
		? Math.max(
				0,
				countdown
					? Math.ceil((Date.parse(startedAt) - now) / 1000)
					: Math.floor((now - Date.parse(startedAt)) / 1000),
			)
		: 0;
}
export function callStatusText(status: string, reason?: string | null): string {
	if (reason === 'caller_cancelled') return 'Вызов отменён';
	return (
		(
			{
				INITIATED: 'Вызов',
				IN_PROGRESS: 'Разговор',
				ENDED: 'Звонок завершён',
				NO_ANSWER: 'Пропущенный звонок',
				REJECTED: 'Звонок отклонён',
			} as Record<string, string>
		)[status] ?? 'Звонок завершён'
	);
}

export function callEndExplanation(status: string, reason?: string | null): string | null {
	if (status !== 'ENDED') return null;
	// API объединяет сбои медиакомпонентов, поэтому точную причину по этому коду не определить.
	switch (reason) {
		case 'preparation_failed':
		case 'setup_timeout':
			return 'Не удалось подготовить защищённый разговор. Звонок завершён автоматически.';
		case 'join_timeout':
			return 'Подключение к разговору не завершилось. Звонок завершён автоматически.';
		case 'reconnect_timeout':
			return 'Не удалось восстановить соединение с участником. Звонок завершён автоматически.';
		case 'media_guard_unavailable':
			return 'Не удалось подтвердить работу обязательных сервисов звонка. Звонок завершён автоматически.';
		case 'transcription_failed':
			return 'Распознавание речи завершилось с ошибкой. Звонок завершён автоматически.';
		case 'recording_failed':
			return 'Запись разговора прервалась. Звонок завершён автоматически.';
		case 'controller_restarted':
			return 'Сервис звонков перезапущен. Звонок завершён автоматически.';
		case 'controller_stopped':
			return 'Сервис звонков остановлен. Звонок завершён автоматически.';
		case 'media_failed':
			return 'В сервисе звонков произошёл сбой. Звонок завершён автоматически.';
		case 'controller_ended':
			return 'Звонок завершён автоматически.';
		case 'feature_disabled':
			return 'Звонки временно отключены. Звонок завершён автоматически.';
		case 'consent_invalid':
			return 'Не удалось подтвердить действующие условия записи. Звонок завершён автоматически.';
		default:
			return null;
	}
}

/** Первые буквы двух первых слов; кавычки и знаки вроде «·» словами не считаются. */
export function callInitials(name: string): string {
	return name
		.split(/[\s-]+/)
		.map((word) => /[\p{L}\p{N}]/u.exec(word)?.[0] ?? '')
		.filter(Boolean)
		.slice(0, 2)
		.join('')
		.toUpperCase();
}

export function callTitle(call: Call): string {
	return call.counterpartName ?? call.warehouseName;
}

/**
 * Кто на той стороне: роль и компания собеседника, затем склад и откуда звонок. Компанию
 * выбирает сторона смотрящего, а не направление звонка: направление говорит, кто звонил,
 * а окно открыто у обоих, и звонившему по направлению досталась бы своя же компания.
 */
export function callDetails(call: Call, counterpartIsSeller: boolean): string[] {
	const company = counterpartIsSeller
		? call.sellerCompanyName
		: call.ffCompanyName || call.warehouseName;
	// Пока собеседник не ответил, склад и есть заголовок (callTitle) — строкой ниже он был бы повтором.
	const warehouse = call.counterpartName ? call.warehouseName : null;
	return [
		[call.counterpartRole, company].filter(Boolean).join(' · '),
		[warehouse, call.sourceLabel].filter(Boolean).join(' · '),
	].filter(Boolean);
}
